/**
 * 广告管理·实体历史（花费 / ROAS 趋势）的**口径与骨架**（2026-10-04 第四批用户口径）。
 *
 * 用户原话（截图标注在花费金额与 ROAS 列上）：
 * 「点击可以查看历史消耗和变化情况，按自然月（最近 12 个月）、按天（最近 30 天）一起显示」。
 * 追问后**明确选 A：弹窗，上下两块**（上面 12 个自然月、下面 30 天）。
 *
 * 本文件只做两件事，全部是零依赖纯函数：
 *  1. **骨架**：把「最近 N 个自然月 / 最近 N 天」的连续轴生成出来
 *     （含 Year/Month 跨年、闰年 2 月、月末）。
 *  2. **聚合**：把「实体 × 业务日」的日粒度事实行聚成月桶 / 日桶，
 *     比率口径与列表**逐字一致**（分母 0 → `null`，绝不 `0`/`NaN`/`Infinity`）。
 *
 * ## 六条口径（与列表端点同源，写进注释避免读端各自理解）
 *
 *  1. **业务日**：事实行的 `date`（`YYYY-MM-DD`），不在本层做时区换算
 *     （跨平台时区校准是写入侧的事，见 R17/R18）。
 *  2. **单一层级**：调用方只查一个 `level`（campaign / adset / ad）的事实，
 *     绝不跨层求和（R30：跨层会把花费按层级数翻倍）。
 *  3. **金额 = 整数分**；`spendUsdCents` 是可选折算列，**NULL 绝不折成 0**：
 *     一个桶里只要有任一日缺折算值，该桶的 `spendUsdCents` 就是 `null`
 *     （部分求和会给出一个看着合理、实际低估的数）。
 *  4. **跨币种不相加**：本函数**只**统计与传入 `currency` 相同的行；其余行忽略
 *     （调用方另行统计并如实告警）。
 *  5. **分母 0 → `null`**：`roas = 归因收入 ÷ 花费`，花费为 0 时是 `null`。
 *     分子 0 而分母非 0 → `0` 是真实结论。
 *  6. **`hasFacts`**：该桶内是否存在事实行。`false` 时 `spendCents` 是 0，但那表示
 *     「窗口内没有事实行」（可能没投、也可能还没同步），读端必须区分，不能当成
 *     「确实花了 0」。
 */

import type { Cents, CurrencyCode, IsoDate } from "./metrics.js";

/** 历史端点支持的层级（与广告树一致，不含 account）。 */
export type AdTreeHistoryLevel = "campaign" | "adset" | "ad";

/** 默认月份窗口：最近 12 个自然月（含当月）。 */
export const AD_TREE_HISTORY_MONTHS = 12;

/** 默认天数窗口：最近 30 天（含 `to` 当天）。 */
export const AD_TREE_HISTORY_DAYS = 30;

/** 一个桶（月 / 日通用）的原始量与派生比率。 */
export interface AdTreeHistoryBucket {
  readonly spendCents: Cents;
  /**
   * 折算 USD（分）；`null` = 没有可用折算值（**不是 0**）。
   * 桶内任一日缺失 → 整个桶 `null`（不部分求和）。
   */
  readonly spendUsdCents: Cents | null;
  /** 口径 1（Meta 自报）的平台归因收入。 */
  readonly metaAttributedRevenueCents: Cents;
  /** 购买数（已按优先级取首个购买类 action）。 */
  readonly platformPurchases: number;
  /** 口径 1 的 ROAS = 归因收入 ÷ 花费；花费 0 → `null`。 */
  readonly roas: number | null;
  /** 该桶内 `spendCents > 0` 的业务日个数。 */
  readonly deliveryDays: number;
  /** 该桶内是否有事实行（`false` 时 `spendCents` 的 0 是「无数据」而不是「花了 0」）。 */
  readonly hasFacts: boolean;
}

export interface AdTreeHistoryMonthPoint extends AdTreeHistoryBucket {
  /** `YYYY-MM`（自然月，含当月）。 */
  readonly month: string;
}

export interface AdTreeHistoryDayPoint extends AdTreeHistoryBucket {
  /** `YYYY-MM-DD`（业务日）。 */
  readonly date: IsoDate;
}

/** 一天的事实输入（已锁单层 / TOTAL / meta_attributed；由 store 的 SQL 产出）。 */
export interface AdTreeHistoryDailyFact {
  readonly date: IsoDate;
  readonly currency: CurrencyCode;
  readonly spendCents: Cents;
  readonly spendUsdCents: Cents | null;
  readonly metaAttributedRevenueCents: Cents;
  readonly platformPurchases: number;
}

export interface AdTreeHistorySeries {
  /** 最近 12 个自然月（含当月），**连续无缺口**（空月也出现，`hasFacts=false`）。 */
  readonly months: readonly AdTreeHistoryMonthPoint[];
  /** 最近 30 天（含 `to`），**连续无缺口**（空日也出现，`hasFacts=false`）。 */
  readonly days: readonly AdTreeHistoryDayPoint[];
  /** 因币种与请求币种不一致而被忽略的日行数（如实暴露，绝不悄悄相加）。 */
  readonly ignoredCurrencyRows: number;
}

/** `YYYY-MM-DD` 加减天数（纯 UTC 序数算术，不受宿主时区/DST 影响）。 */
export function shiftIsoDate(date: IsoDate, deltaDays: number): IsoDate {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  const shifted = new Date(Date.UTC(year, month - 1, day) + deltaDays * 86_400_000);
  return formatIsoDate(shifted);
}

function formatIsoDate(date: Date): IsoDate {
  return (
    `${String(date.getUTCFullYear()).padStart(4, "0")}-` +
    `${String(date.getUTCMonth() + 1).padStart(2, "0")}-` +
    `${String(date.getUTCDate()).padStart(2, "0")}`
  );
}

/** `YYYY-MM` 加/减月（只在「年 × 12 + 月」上做整数运算，不碰日，月末不会溢出）。 */
export function shiftMonth(month: string, deltaMonths: number): string {
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7)) - 1;
  const shifted = year * 12 + monthIndex + deltaMonths;
  const shiftedYear = Math.floor(shifted / 12);
  const shiftedMonth = shifted - shiftedYear * 12 + 1;
  return `${String(shiftedYear).padStart(4, "0")}-${String(shiftedMonth).padStart(2, "0")}`;
}

/** 业务日的自然月键（`YYYY-MM-DD` → `YYYY-MM`）。 */
export function businessMonthOf(date: IsoDate): string {
  return date.slice(0, 7);
}

/**
 * 最近 N 个自然月（含 `to` 所在月），**升序**（最旧 → 最新）。
 *
 * `to = "2026-01-15"`、`count = 12` → `["2025-02", …, "2026-01"]`。
 * `count <= 0` → `[]`（不抛错；调用方给 0 就是不要骨架）。
 */
export function recentMonthKeys(to: IsoDate, count: number = AD_TREE_HISTORY_MONTHS): string[] {
  if (!Number.isSafeInteger(count) || count <= 0) return [];
  const current = businessMonthOf(to);
  const out: string[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) out.push(shiftMonth(current, -offset));
  return out;
}

/**
 * 最近 N 天（含 `to` 当天），**升序**（最旧 → 最新）。
 *
 * `to = "2024-03-01"`、`count = 30` → 从 `2024-02-01` 到 `2024-03-01`
 * （闰年 2 月 29 天也正确）。
 */
export function recentBusinessDates(to: IsoDate, count: number = AD_TREE_HISTORY_DAYS): IsoDate[] {
  if (!Number.isSafeInteger(count) || count <= 0) return [];
  const out: IsoDate[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) out.push(shiftIsoDate(to, -offset));
  return out;
}

/** 桶内求和（防御性：同一业务日出现多行时相加，而不是取最后一行）。 */
interface BucketAccumulator {
  spendCents: number;
  revenueCents: number;
  purchases: number;
  usdCents: number | null;
  usdComplete: boolean;
  daysWithSpend: number;
  factDays: number;
}

function emptyAccumulator(): BucketAccumulator {
  return {
    spendCents: 0,
    revenueCents: 0,
    purchases: 0,
    usdCents: 0,
    usdComplete: true,
    daysWithSpend: 0,
    factDays: 0,
  };
}

function accumulate(bucket: BucketAccumulator, fact: AdTreeHistoryDailyFact): void {
  bucket.spendCents += fact.spendCents;
  bucket.revenueCents += fact.metaAttributedRevenueCents;
  bucket.purchases += fact.platformPurchases;
  if (fact.spendUsdCents === null) bucket.usdComplete = false;
  else bucket.usdCents = (bucket.usdCents ?? 0) + fact.spendUsdCents;
  if (fact.spendCents > 0) bucket.daysWithSpend += 1;
  bucket.factDays += 1;
}

function finalize(bucket: BucketAccumulator): AdTreeHistoryBucket {
  return {
    spendCents: bucket.spendCents,
    // 任一日缺折算 → 整个桶 null（部分求和是「看着合理但低估」的那种错）。
    spendUsdCents: bucket.factDays === 0 || !bucket.usdComplete ? null : bucket.usdCents,
    metaAttributedRevenueCents: bucket.revenueCents,
    platformPurchases: bucket.purchases,
    roas: adTreeHistoryRoas(bucket.revenueCents, bucket.spendCents),
    deliveryDays: bucket.daysWithSpend,
    hasFacts: bucket.factDays > 0,
  };
}

/**
 * 口径 1 的 ROAS：`归因收入 ÷ 花费`；**分母 0（或非有限）→ `null`**。
 *
 * 与 `metrics-calc.ts` 的 `deriveMetrics().metaRoas` 同一条红利线；
 * 单独再写一个是为了让「月桶 / 日桶」不依赖构造一个完整 `MetricBucket`。
 */
export function adTreeHistoryRoas(revenueCents: number, spendCents: number): number | null {
  if (!Number.isFinite(revenueCents) || !Number.isFinite(spendCents)) return null;
  if (spendCents <= 0) return null;
  return revenueCents / spendCents;
}

export interface BuildAdTreeHistorySeriesInput {
  /** 窗口上界（业务日，闭区间）。缺省由调用方给「今天」。 */
  readonly to: IsoDate;
  /** 该实体的结算币种（`meta_accounts.currency`）。 */
  readonly currency: CurrencyCode;
  /** 日粒度事实行（已锁单层 / TOTAL / meta_attributed）。 */
  readonly daily: readonly AdTreeHistoryDailyFact[];
  readonly monthCount?: number;
  readonly dayCount?: number;
}

/**
 * 由日粒度事实行构建「12 个月 + 30 天」两块**连续**序列。
 *
 * 缺月 / 缺日**不补 0**：它们是 `hasFacts=false` 的空桶，`spendCents=0`
 * 只作为绘图基线，读端看到 `hasFacts=false` 必须显示「无数据」而不是「花了 $0」。
 */
export function buildAdTreeHistorySeries(input: BuildAdTreeHistorySeriesInput): AdTreeHistorySeries {
  const monthCount = input.monthCount ?? AD_TREE_HISTORY_MONTHS;
  const dayCount = input.dayCount ?? AD_TREE_HISTORY_DAYS;
  const currency = input.currency.trim().toUpperCase();

  const monthKeys = recentMonthKeys(input.to, monthCount);
  const dayKeys = recentBusinessDates(input.to, dayCount);
  const monthAcc = new Map<string, BucketAccumulator>();
  const dayAcc = new Map<string, BucketAccumulator>();
  for (const key of monthKeys) monthAcc.set(key, emptyAccumulator());
  for (const key of dayKeys) dayAcc.set(key, emptyAccumulator());

  let ignoredCurrencyRows = 0;
  for (const fact of input.daily) {
    if (fact.currency.trim().toUpperCase() !== currency) {
      ignoredCurrencyRows += 1;
      continue;
    }
    const monthBucket = monthAcc.get(businessMonthOf(fact.date));
    // 只累加落在骨架里的日：越界行（例如 SQL 多取了一天）不产生"幽灵桶"。
    if (monthBucket !== undefined) accumulate(monthBucket, fact);
    const dayBucket = dayAcc.get(fact.date);
    if (dayBucket !== undefined) accumulate(dayBucket, fact);
  }

  return {
    months: monthKeys.map((month) => ({ month, ...finalize(monthAcc.get(month) ?? emptyAccumulator()) })),
    days: dayKeys.map((date) => ({ date, ...finalize(dayAcc.get(date) ?? emptyAccumulator()) })),
    ignoredCurrencyRows,
  };
}
